import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Check, Trash2 } from "lucide-react";
import {
  ALL_DAY_SLOT,
  DateAvailability,
  SLOT_PRESETS,
  SlotType,
  TimeSlot,
} from "./types";
import { findConflict } from "./availabilityUtils";
import MorningIcon from "@/assets/icons/MorningIcon.svg";
import AfternoonIcon from "@/assets/icons/AfternoonIcon.svg";
import EveningIcon from "@/assets/icons/Evening.svg";
import LateNightIcon from "@/assets/icons/LateNightIcon.svg";
import CustomIcon from "@/assets/icons/Custom.svg";
import MinimalSelect from "./MinimalSelect";

const makeId = () => crypto.randomUUID();

const slotIcons: Record<string, React.ReactNode> = {
  morning: <MorningIcon />,
  afternoon: <AfternoonIcon />,
  evening: <EveningIcon />,
  late_night: <LateNightIcon />,
};

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

const formatTo12h = (time: string) => {
  const [hh, mm] = time.split(":").map(Number);
  const suffix = hh >= 12 ? "PM" : "AM";
  const hour = hh % 12 === 0 ? 12 : hh % 12;
  return `${hour}:${String(mm).padStart(2, "0")} ${suffix}`;
};

export default function DaySlotEditor({
  dateAvail,
  onSave,
}: {
  dateAvail: DateAvailability | null;
  onSave: (updated: DateAvailability) => void;
}) {
  const [draftAllDay, setDraftAllDay] = useState(false);
  const [draftSlots, setDraftSlots] = useState<TimeSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [backupSlots, setBackupSlots] = useState<TimeSlot[]>([]);

  // Load draft when active date changes
  useEffect(() => {
    if (!dateAvail) return;
    setDraftAllDay(dateAvail.isAllDay);
    setDraftSlots(dateAvail.timeSlots || []);
    setError(null);
  }, [dateAvail?.date?.toISOString()]);

  // Live conflict check
  useEffect(() => {
    if (draftAllDay) {
      setError(null);
      return;
    }
    const conflict = findConflict(draftSlots);
    setError(conflict?.message || null);
  }, [draftSlots, draftAllDay]);

  const pushToParent = (isAll: boolean, slots: TimeSlot[]) => {
    if (!dateAvail) return;

    if (!isAll) {
      const conflict = findConflict(slots);
      if (conflict) {
        setError(conflict.message);
        return;
      }
    }

    const finalSlots = isAll
      ? [
          {
            ...ALL_DAY_SLOT,
            id: slots?.[0]?.id || makeId(),
          },
        ]
      : slots;

    onSave({
      ...dateAvail,
      isAllDay: isAll,
      timeSlots: finalSlots,
    });
    setError(null);
  };

  const title = useMemo(() => {
    if (!dateAvail) return "";
    return format(new Date(dateAvail.date), "EEE, do MMM");
  }, [dateAvail]);

  if (!dateAvail) return null;

  const existingTypes = new Set(draftSlots.map((s) => s.slotType));
  const visiblePresets = SLOT_PRESETS.filter(
    (p) => !existingTypes.has(p.slotType),
  );

  const addPreset = (preset: {
    slotType: SlotType;
    label: string;
    start: string;
    end: string;
  }) => {
    const nextSlots: TimeSlot[] = [
      ...draftSlots,
      {
        id: makeId(),
        start: preset.start,
        end: preset.end,
        slotType: preset.slotType,
        label: preset.label,
      },
    ];
    setDraftAllDay(false);
    setDraftSlots(nextSlots);
    pushToParent(false, nextSlots);
  };

  const addCustom = () => {
    const nextSlots: TimeSlot[] = [
      ...draftSlots,
      {
        id: makeId(),
        start: "09:00",
        end: "11:00",
        slotType: "custom",
        label: "Custom",
      },
    ];
    setDraftAllDay(false);
    setDraftSlots(nextSlots);
    pushToParent(false, nextSlots);
  };

  const updateSlot = (id: string, key: "start" | "end", value: string) => {
    const next = draftSlots.map((s) =>
      s.id === id ? { ...s, [key]: value } : s,
    );
    setDraftSlots(next);
    pushToParent(draftAllDay, next);
  };

  const removeSlot = (id: string) => {
    const next = draftSlots.filter((s) => s.id !== id);
    setDraftSlots(next);
    pushToParent(draftAllDay, next);
  };

  const toggleAllDay = () => {
    const next = !draftAllDay;

    if (next) {
      setBackupSlots(draftSlots);
      const nextSlots: TimeSlot[] = [
        {
          ...ALL_DAY_SLOT,
          id: crypto.randomUUID(),
        },
      ];
      setDraftAllDay(true);
      setDraftSlots(nextSlots);
      pushToParent(true, nextSlots);
    } else {
      const nextSlots = backupSlots.length ? backupSlots : [];
      setDraftAllDay(false);
      setDraftSlots(nextSlots);
      pushToParent(false, nextSlots);
    }
  };

  const parts = title.split(/(\d+)/);

  return (
    <div className="border-t border-[#DBDDDF] pt-4">
      <div className="bg-[#F9FAFB] py-2 px-3 mb-4 rounded-[8px]">
        <p className="text-[#475467] text-xs font-medium">
          Your profile becomes visible to clients only during your selected
          slots. Update your availability to receive more opportunities.
        </p>
      </div>

      <div className="rounded-[8px] bg-[#FFF9F5] py-3 px-3 sm:px-[13px] space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm sm:text-base font-medium text-[#344054]">
            {parts.map((part, index) =>
              /\d+/.test(part) ? (
                <span key={index} className="text-[#FF6600]">
                  {part}
                </span>
              ) : (
                <span key={index}>{part}</span>
              ),
            )}
          </p>

          <label className="flex items-center gap-2 text-sm text-[#475467] cursor-pointer">
            <input
              type="checkbox"
              checked={draftAllDay}
              onChange={toggleAllDay}
              className="sr-only"
            />
            <div
              className={`
            w-5 h-5 rounded-[6px] border border-[#D0D5DD]
            flex items-center justify-center
            ${draftAllDay ? "bg-[#FF6600] border-[#FF6600]" : "bg-white"}
          `}
            >
              {draftAllDay && <Check size={14} color="white" />}
            </div>
            I’m available All-day
          </label>
        </div>

        {/* Slots */}
        {!draftAllDay && draftSlots.length > 0 && (
          <div className="space-y-3">
            {draftSlots.map((slot) => (
              <div
                key={slot.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <span className="px-3 py-1 rounded-full border border-[#FFB98F] text-[#ED5E00] text-sm font-medium bg-white w-fit">
                  {slot.label}
                </span>

                <div className="flex sm:items-center gap-2 sm:ml-auto w-full sm:w-auto">
                  <MinimalSelect
                    value={slot.start}
                    options={TIME_OPTIONS}
                    format={formatTo12h}
                    onChange={(val) => updateSlot(slot.id, "start", val)}
                  />

                  <span className="text-xs text-[#667085] hidden sm:block">
                    to
                  </span>

                  <MinimalSelect
                    value={slot.end}
                    options={TIME_OPTIONS}
                    format={formatTo12h}
                    onChange={(val) => updateSlot(slot.id, "end", val)}
                  />

                  <button
                    type="button"
                    onClick={() => removeSlot(slot.id)}
                    className="p-2 text-[#F04438] hover:bg-[#F04438]/10 rounded-md self-start sm:self-auto"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {!draftAllDay && (
          <div className="pt-6 border-t border-[#DBDDDF]">
            <p className="text-xs text-[#989898] mb-3">
              Available Slots for {format(new Date(dateAvail.date), "do MMM")}
            </p>

            <div className="flex flex-wrap gap-2 sm:gap-3">
              {visiblePresets.map((p) => (
                <button
                  key={p.slotType}
                  type="button"
                  onClick={() => addPreset(p)}
                  className="px-3 sm:px-4 py-2 rounded-full bg-white border border-[#E4E7EC] hover:border-[#FFB98F] flex items-center gap-2 text-sm"
                >
                  {slotIcons[p.slotType]}

                  <span className="font-medium text-[#344054]">{p.label}</span>

                  <span className="text-[#989898] text-xs hidden sm:block">
                    {formatTo12h(p.start)} – {formatTo12h(p.end)}
                  </span>
                </button>
              ))}

              <button
                type="button"
                onClick={addCustom}
                className="px-3 sm:px-4 py-2 rounded-full bg-white border border-[#EAECF0] text-sm text-[#344054] hover:border-[#FFB98F] flex items-center gap-2"
              >
                <CustomIcon />
                Custom
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
